import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MonitoringComponent } from './components/monitoring/monitoring.component';
import { MeteorologicalDataComponent } from './components/meteorological-data/meteorological-data.component';
import { SadSummaryComponent } from './components/sad-summary/sad-summary.component';
import { StationApiService } from '../../../stations/services/api/station-api.service';
import { MissingStationDataManagementApiService } from '../../../stations/services/api/station-missing-data-daily-api.service';
import { LoggingService } from '../../../../core/services/logging/logging.service';
import { StationsModel } from '../../../stations/models/stations';
import { GeolocationService } from '../../../../shared/services/geolocation/geolocation.service';
import { HomePageApiService } from './services/api/home-page-api.service';
import { NearestStationModel } from './models/nearest-station';
import { StationDetailModel } from './models/station-detail';
import { CoffeeRustAlertApiService } from '../../../sad/services/api/coffee-rust-alert.api.service';
import { CoffeePhomaAlertApiService } from '../../../sad/services/api/coffee-phoma-alert.api.service';
import { NonConformitiesApiService } from '../../../non-conformities/services/api/non-conformities-api.service';

@Component({
  selector: 'app-home-page',
  standalone: true,
  imports: [
    CommonModule,
    MonitoringComponent,
    MeteorologicalDataComponent,
    SadSummaryComponent,
  ],
  templateUrl: './home-page.component.html',
  styleUrls: ['./home-page.component.css'],
})
export class HomePageComponent implements OnInit {
  // Propriedades para Station Summary
  stationsWithConnectionIssuesCount: number = 0;
  missingDataCount: number = 0;
  nonConformitiesCount: number = 0;
  isLoadingConnectionIssues: boolean = true;
  isLoadingMissingData: boolean = true;
  isLoadingNonConformities: boolean = true;

  // Propriedades para SAD (Sistema de Alerta de Doenças)
  rustAlertCount: number = 0;
  phomaAlertCount: number = 0;
  isLoadingRustAlert: boolean = true;
  isLoadingPhomaAlert: boolean = true;

  // Propriedades para Meteorological Data
  averageStations: StationsModel[] = [];

  // Propriedades para Estação Mais Próxima
  nearestStation: NearestStationModel | null = null;
  nearestStationIdentifier: string | null = null;
  nearestStationDetail: StationDetailModel | null = null;
  isLoadingNearestStation: boolean = false;

  constructor(
    private readonly stationApi: StationApiService,
    private readonly missingDataApi: MissingStationDataManagementApiService,
    private readonly logger: LoggingService,
    private readonly geolocationService: GeolocationService,
    private readonly homePageApi: HomePageApiService,
    private readonly coffeeRustAlertApi: CoffeeRustAlertApiService,
    private readonly coffeePhomaAlertApi: CoffeePhomaAlertApiService,
    private readonly nonConformitiesApi: NonConformitiesApiService,
  ) {}

  ngOnInit() {
    this.loadDisconnectedStationsCount();
    this.loadMissingDataCount();
    this.loadNonConformitiesCount();
    this.loadAverageStations();
    this.loadNearestStation();
    this.loadSADData();
  }

  /**
   * Carrega a quantidade de estações SEM comunicação usando o endpoint dedicado.
   */
  private loadDisconnectedStationsCount(): void {
    this.isLoadingConnectionIssues = true;
    this.stationApi.getDisconnectedStationsCount().subscribe({
      next: (count: number) => {
        this.stationsWithConnectionIssuesCount = count ?? 0;
        this.isLoadingConnectionIssues = false;
      },
      error: (err) => {
        this.logger.error(
          'Erro ao buscar total de estações sem comunicação:',
          err,
        );
        this.stationsWithConnectionIssuesCount = 0;
        this.isLoadingConnectionIssues = false;
      },
    });
  }

  /**
   * Carrega o total de inconformidades não resolvidas
   */
  private loadNonConformitiesCount(): void {
    this.isLoadingNonConformities = true;
    this.nonConformitiesApi.getUnresolvedCount().subscribe({
      next: (count: number) => {
        this.nonConformitiesCount = count;
        this.isLoadingNonConformities = false;
      },
      error: (err) => {
        this.logger.error('Erro ao buscar total de inconformidades:', err);
        this.nonConformitiesCount = 0;
        this.isLoadingNonConformities = false;
      },
    });
  }

  /**
   * Carrega a quantidade de dados faltantes nos últimos 30 dias
   */
  private loadMissingDataCount(): void {
    this.isLoadingMissingData = true;
    this.missingDataApi.getMissingDataCount().subscribe({
      next: (count: number) => {
        this.missingDataCount = count;
        this.isLoadingMissingData = false;
      },
      error: (err) => {
        this.logger.error('Erro ao buscar dados faltantes:', err);
        this.missingDataCount = 0; // Valor padrão em caso de erro
        this.isLoadingMissingData = false;
      },
    });
  }

  /**
   * Carrega a lista de estações para o filtro de médias
   * Ordenadas por status (ativas primeiro) e depois por descrição
   */
  private loadAverageStations(): void {
    this.homePageApi.getAllStations().subscribe({
      next: (stations: StationsModel[]) => {
        // Ordena estações: ativas primeiro, depois inativas. Ambas em ordem alfabética
        this.averageStations = [...stations].sort((a, b) => {
          if (a.active && !b.active) return -1;
          if (!a.active && b.active) return 1;

          // Ordenação secundária por descrição (alfabética)
          const descA = (a.description || `Estação ${a.id}`).toLowerCase();
          const descB = (b.description || `Estação ${b.id}`).toLowerCase();
          return descA.localeCompare(descB);
        });
      },
      error: (err: any) => {
        this.logger.error('Erro ao carregar estações:', err);
      },
    });
  }

  /**
   * Busca a estação meteorológica mais próxima com base na geolocalização do usuário
   */
  loadNearestStation(): void {
    // Verifica se a geolocalização está disponível
    if (!this.geolocationService.isGeolocationAvailable()) {
      this.logger.warn('Geolocalização não está disponível no navegador');
      return;
    }

    this.isLoadingNearestStation = true;

    // Obtém a posição atual do usuário
    this.geolocationService.getCurrentPosition().subscribe({
      next: (coordinates) => {
        // Busca a estação mais próxima usando as coordenadas
        this.homePageApi
          .getNearestStation({
            latitude: coordinates.latitude,
            longitude: coordinates.longitude,
          })
          .subscribe({
            next: (station: NearestStationModel) => {
              this.nearestStation = station;
              this.nearestStationIdentifier = station.identifier;

              // Busca os detalhes completos da estação usando o ID
              this.loadNearestStationDetails(station.id);
            },
            error: (err: any) => {
              this.logger.error('Erro ao buscar estação mais próxima:', err);
              this.isLoadingNearestStation = false;
            },
          });
      },
      error: (err: any) => {
        this.logger.error('Erro ao obter localização:', err);
        this.isLoadingNearestStation = false;
      },
    });
  }

  /**
   * Busca os detalhes completos da estação mais próxima
   * @param stationId ID da estação
   */
  private loadNearestStationDetails(stationId: number | string): void {
    this.homePageApi.getStationById(stationId).subscribe({
      next: (stationDetail: StationDetailModel) => {
        this.nearestStationDetail = stationDetail;
        this.isLoadingNearestStation = false;
      },
      error: (err: any) => {
        this.logger.error('Erro ao buscar detalhes da estação:', err);
        this.isLoadingNearestStation = false;
      },
    });
  }

  /**
   * Carrega os dados do Sistema de Alerta de Doenças (SAD)
   */
  private loadSADData(): void {
    // Carrega alertas de ferrugem do café
    this.isLoadingRustAlert = true;
    this.coffeeRustAlertApi.getActiveAlerts().subscribe({
      next: (response) => {
        this.rustAlertCount = response.favorableAndVeryFavorableAlertsCount;
        this.isLoadingRustAlert = false;
      },
      error: () => {
        this.rustAlertCount = 0;
        this.isLoadingRustAlert = false;
      },
    });

    // Carrega alertas de phoma do café
    this.isLoadingPhomaAlert = true;
    this.coffeePhomaAlertApi.getActiveAlerts().subscribe({
      next: (response) => {
        this.phomaAlertCount = response.favorableAndVeryFavorableAlertsCount;
        this.isLoadingPhomaAlert = false;
      },
      error: () => {
        this.phomaAlertCount = 0;
        this.isLoadingPhomaAlert = false;
      },
    });
  }
}
